/**
 * useFreePlayLeaderboard Hook
 * Fetch free play and all-time leaderboards
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { FreePlayLeaderboardEntry, LeaderboardScope } from "../types";

interface UseFreePlayLeaderboardReturn {
  /** Leaderboard entries */
  leaderboard: FreePlayLeaderboardEntry[];
  /** Current user's entry */
  myEntry: FreePlayLeaderboardEntry | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Change scope filter */
  setScope: (scope: LeaderboardScope) => void;
  /** Current scope */
  scope: LeaderboardScope;
  /** Refetch leaderboard */
  refetch: () => Promise<void>;
}

interface UseFreePlayLeaderboardOptions {
  /** Initial scope (default: 'all_time') */
  initialScope?: LeaderboardScope;
  /** Game slug to filter by (null for overall) */
  gameSlug?: string | null;
  /** Limit results (default: 50) */
  limit?: number;
}

/**
 * Hook to fetch free play leaderboards
 */
export const useFreePlayLeaderboard = (
  options: UseFreePlayLeaderboardOptions = {}
): UseFreePlayLeaderboardReturn => {
  const { 
    initialScope = "all_time", 
    gameSlug = null,
    limit = 50 
  } = options;

  const { activeProfile, activeWeekend } = useAuth();
  const userId = activeProfile?.id;

  const [leaderboard, setLeaderboard] = useState<FreePlayLeaderboardEntry[]>([]);
  const [myEntry, setMyEntry] = useState<FreePlayLeaderboardEntry | null>(null);
  const [scope, setScope] = useState<LeaderboardScope>(initialScope);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Calculate date filter based on scope
   */
  const getDateFilter = useCallback((currentScope: LeaderboardScope): string | null => {
    const now = new Date();
    
    switch (currentScope) {
      case "weekly": {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return weekAgo.toISOString();
      }
      case "monthly": {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return monthAgo.toISOString();
      }
      case "all_time":
      default:
        return null;
    }
  }, []);

  /**
   * Fetch leaderboard data
   */
  const fetchLeaderboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query for user_game_stats
      let query = supabase
        .from("user_game_stats")
        .select(`
          user_id,
          game_slug,
          games_played,
          games_won,
          total_points_earned,
          personal_best_score,
          current_streak,
          last_played_at,
          user:profiles!user_game_stats_user_id_fkey(
            id, 
            full_name, 
            avatar_url
          )
        `)
        .order("total_points_earned", { ascending: false })
        .limit(limit);

      // Filter by game if specified
      if (gameSlug) {
        query = query.eq("game_slug", gameSlug);
      }

      // Filter by weekend if scope is 'weekend'
      if (scope === "weekend" && activeWeekend?.id) {
        query = query.eq("weekend_id", activeWeekend.id);
      } else {
        // For free play, we want entries without weekend_id
        query = query.is("weekend_id", null);
      }

      // Filter by date if weekly/monthly
      const dateFilter = getDateFilter(scope);
      if (dateFilter) {
        query = query.gte("last_played_at", dateFilter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Get team info for users
      const userIds = (data || []).map((entry: { user_id: string }) => entry.user_id);
      
      let teamMemberships: Record<string, { team_id: string; team_name: string; team_color: string }> = {};
      
      if (userIds.length > 0 && activeWeekend?.id) {
        const { data: teamData } = await supabase
          .from("team_members")
          .select(`
            user_id,
            team:teams!team_members_team_id_fkey(id, name, color)
          `)
          .in("user_id", userIds);

        if (teamData) {
          teamMemberships = teamData.reduce((acc, tm) => {
            // Handle joined data - ensure it's an object not array
            const team = tm.team && typeof tm.team === 'object' && !Array.isArray(tm.team) 
              ? tm.team as { id: string; name: string; color: string }
              : null;
            if (team) {
              acc[tm.user_id] = {
                team_id: team.id,
                team_name: team.name,
                team_color: team.color,
              };
            }
            return acc;
          }, {} as Record<string, { team_id: string; team_name: string; team_color: string }>);
        }
      }

      // Transform to leaderboard entries
      const entries: FreePlayLeaderboardEntry[] = (data || []).map((entry, index) => {
        // Handle joined data - ensure it's an object not array
        const userData = entry.user;
        const userProfile = userData && typeof userData === 'object' && !Array.isArray(userData)
          ? userData as { id: string; full_name: string; avatar_url: string | null }
          : null;
        const teamInfo = teamMemberships[entry.user_id];
        const winRate = entry.games_played > 0 
          ? Math.round((entry.games_won / entry.games_played) * 100) 
          : 0;

        return {
          rank: index + 1,
          user_id: entry.user_id,
          full_name: userProfile?.full_name || "Unknown",
          avatar_url: userProfile?.avatar_url || null,
          team_id: teamInfo?.team_id || null,
          team_name: teamInfo?.team_name || null,
          team_color: teamInfo?.team_color || null,
          games_played: entry.games_played,
          games_won: entry.games_won,
          win_rate: winRate,
          total_points: entry.total_points_earned,
          best_score: entry.personal_best_score || 0,
          current_streak: entry.current_streak,
        };
      });

      setLeaderboard(entries);

      // Find current user's entry
      if (userId) {
        const myRank = entries.find((e) => e.user_id === userId);
        setMyEntry(myRank || null);
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch leaderboard"));
    } finally {
      setIsLoading(false);
    }
  }, [scope, gameSlug, userId, activeWeekend, limit, getDateFilter]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return {
    leaderboard,
    myEntry,
    isLoading,
    error,
    setScope,
    scope,
    refetch: fetchLeaderboard,
  };
};

/**
 * Get per-game leaderboards for all games
 */
export const useAllGamesLeaderboard = (options: { limit?: number } = {}) => {
  const { limit = 10 } = options;

  const [gameLeaderboards, setGameLeaderboards] = useState<
    Record<string, FreePlayLeaderboardEntry[]>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAllLeaderboards = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get all active games
      const { data: games, error: gamesError } = await supabase
        .from("game_definitions")
        .select("slug, name")
        .eq("is_active", true);

      if (gamesError) throw gamesError;

      const leaderboards: Record<string, FreePlayLeaderboardEntry[]> = {};

      // Fetch leaderboard for each game
      for (const game of games || []) {
        const { data } = await supabase
          .from("user_game_stats")
          .select(`
            user_id,
            games_played,
            games_won,
            total_points_earned,
            personal_best_score,
            current_streak,
            user:profiles!user_game_stats_user_id_fkey(id, full_name, avatar_url)
          `)
          .eq("game_slug", game.slug)
          .is("weekend_id", null)
          .order("total_points_earned", { ascending: false })
          .limit(limit);

        if (data && data.length > 0) {
          leaderboards[game.slug] = data.map((entry, index) => {
            // Handle joined data - ensure it's an object not array
            const userData = entry.user;
            const userProfile = userData && typeof userData === 'object' && !Array.isArray(userData)
              ? userData as { id: string; full_name: string; avatar_url: string | null }
              : null;
            return {
              rank: index + 1,
              user_id: entry.user_id,
              full_name: userProfile?.full_name || "Unknown",
              avatar_url: userProfile?.avatar_url || null,
              team_id: null,
              team_name: null,
              team_color: null,
              games_played: entry.games_played,
              games_won: entry.games_won,
              win_rate: entry.games_played > 0 
                ? Math.round((entry.games_won / entry.games_played) * 100)
                : 0,
              total_points: entry.total_points_earned,
              best_score: entry.personal_best_score || 0,
              current_streak: entry.current_streak,
            };
          });
        }
      }

      setGameLeaderboards(leaderboards);
    } catch (err) {
      console.error("Error fetching all leaderboards:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch leaderboards"));
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchAllLeaderboards();
  }, [fetchAllLeaderboards]);

  return {
    gameLeaderboards,
    isLoading,
    error,
    refetch: fetchAllLeaderboards,
  };
};

